// src/Login.js
import React, { useState } from "react";
import { signInWithEmailAndPassword, signOut, sendEmailVerification, sendPasswordResetEmail } from "firebase/auth";
import { auth } from "./firebase";
import { useNavigate } from "react-router-dom";

function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showResend, setShowResend] = useState(false);
  const [resendMessage, setResendMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [resetMessage, setResetMessage] = useState("");
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setShowResend(false);
    setResendMessage("");
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      if (!userCredential.user.emailVerified) {
        await signOut(auth);
        setError("Please verify your email before logging in.");
        setShowResend(true);
        return;
      }
      setSuccess("Logged in successfully!");
      setEmail("");
      setPassword("");
      setTimeout(() => {
        navigate("/");
      }, 1000);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleResend = async () => {
    setResendMessage("");
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      await sendEmailVerification(userCredential.user);
      await signOut(auth);
      setResendMessage("Verification email resent. Please check your inbox.");
    } catch (err) {
      setResendMessage("Could not resend verification email. Please check your credentials.");
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setResetMessage("");
    if (!email) {
      setResetMessage("Please enter your email above first.");
      return;
    }
    try {
      await sendPasswordResetEmail(auth, email);
      setResetMessage("Password reset email sent! Please check your inbox.");
    } catch (err) {
      setResetMessage("Could not send reset email. Please check your email address.");
    }
  };

  return (
    <div style={{
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)"
    }}>
      <div style={{
        width: 350,
        background: "#fff",
        borderRadius: 24,
        boxShadow: "0 8px 32px rgba(60,60,120,0.12)",
        padding: "2.5rem 2rem 2rem 2rem",
        display: "flex",
        flexDirection: "column",
        alignItems: "center"
      }}>
        <svg width="64" height="64" style={{ marginBottom: 16 }} viewBox="0 0 24 24" fill="none">
          <rect x="2" y="4" width="20" height="16" rx="4" fill="#e4f8ec"/>
          <path d="M6 8h12M6 12h12M6 16h8" stroke="#1D665D" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
        <h2 style={{ margin: "0 0 8px 0", fontWeight: 700, color: "#222" }}>Sign In</h2>
        <p style={{ color: "#888", fontSize: 15, marginBottom: 24 }}>Please sign in to continue.</p>
        <form onSubmit={handleSubmit} style={{ width: "100%", display: "flex", flexDirection: "column", alignItems: "center" }}>
          <div style={{ marginBottom: 16, width: "100%", position: "relative", maxWidth: 280 }}>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              placeholder="Email"
              style={{
                width: "100%",
                padding: "12px 16px",
                borderRadius: 12,
                border: "1.5px solid #e0e0e0",
                fontSize: 16,
                outline: "none",
                background: "#f7f7f7",
                boxSizing: "border-box"
              }}
            />
          </div>
          <div style={{ marginBottom: 16, width: "100%", position: "relative", maxWidth: 280 }}>
            <input
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              placeholder="Password"
              style={{
                width: "100%",
                padding: "12px 40px 12px 16px",
                borderRadius: 12,
                border: "1.5px solid #e0e0e0",
                fontSize: 16,
                outline: "none",
                background: "#f7f7f7",
                boxSizing: "border-box"
              }}
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              tabIndex={-1}
              style={{
                position: "absolute",
                right: 12,
                top: "50%",
                transform: "translateY(-50%)",
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: 0,
                outline: "none"
              }}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.06 10.06 0 0 1 12 20C7 20 2.73 16.11 1 12c.74-1.61 1.81-3.06 3.06-4.31M9.88 9.88A3 3 0 0 1 12 9c1.66 0 3 1.34 3 3 0 .39-.08.76-.21 1.09" /><path d="M1 1l22 22" /></svg>
              ) : (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12C2.73 7.89 7 4 12 4s9.27 3.89 11 8c-1.73 4.11-6 8-11 8S2.73 16.11 1 12z" /><circle cx="12" cy="12" r="3" /></svg>
              )}
            </button>
          </div>
          <button
            type="submit"
            className="btn btn-primary"
            style={{
              width: 280,
              padding: "12px",
              borderRadius: 12,
              fontWeight: 700,
              fontSize: 17,
              marginTop: 8,
              marginBottom: 8,
              boxShadow: "0 2px 8px rgba(29,102,93,0.08)"
            }}>
            Sign In
          </button>
        </form>
        <div style={{ width: 280, textAlign: "right", marginTop: 4, marginBottom: 8 }}>
          <a href="#" onClick={handleForgotPassword} style={{ color: "#1D665D", fontSize: 14, textDecoration: "underline" }}>Forgot password?</a>
        </div>
        {error && <p style={{ color: "red", marginTop: 10 }}>{error}</p>}
        {success && <p style={{ color: "green", marginTop: 10 }}>{success}</p>}
        {showResend && (
          <div style={{ marginTop: 12 }}>
            <button onClick={handleResend} style={{ background: "none", color: "#1D665D", border: "none", cursor: "pointer", textDecoration: "underline" }}>
              Resend verification email
            </button>
            {resendMessage && <p style={{ color: "#1D665D", marginTop: 6 }}>{resendMessage}</p>}
          </div>
        )}
        {resetMessage && <p style={{ color: "#1D665D", marginTop: 8 }}>{resetMessage}</p>}
        <p style={{ color: "#888", fontSize: 15, marginTop: 18 }}>
          Don't have an account?{" "}
          <a href="/signup" style={{ color: "#1D665D", fontWeight: 600, textDecoration: "none" }}>Sign Up</a>
        </p>
      </div>
    </div>
  );
}

export default Login;